/**
 * Plotly.js charts for real-time data visualization
 */

import Plotly from 'plotly.js-dist-min';
import type { Sample } from '../sim/types';

interface PlotData {
  time: number[];
  omega_x: number[];
  omega_y: number[];
  omega_z: number[];
  energy: number[];
  kinetic: number[];
  potential: number[];
}

export class SimulationPlots {
  private omegaDiv: HTMLElement;
  private energyDiv: HTMLElement;

  private data: PlotData = {
    time: [],
    omega_x: [],
    omega_y: [],
    omega_z: [],
    energy: [],
    kinetic: [],
    potential: []
  };

  private windowSize = 500; // Number of points to display
  private updateCounter = 0;
  private updateInterval = 5; // Update plot every N samples

  constructor(omegaDivId: string, energyDivId: string) {
    this.omegaDiv = document.getElementById(omegaDivId)!;
    this.energyDiv = document.getElementById(energyDivId)!;

    if (!this.omegaDiv || !this.energyDiv) {
      throw new Error('Plot div elements not found');
    }

    this.initializePlots();

    // Add theme change listener
    window.addEventListener('theme-change', () => this.updateTheme());
  }

  private getThemeColors() {
    const style = getComputedStyle(document.documentElement);
    return {
      paper_bgcolor: style.getPropertyValue('--bg-secondary').trim(),
      plot_bgcolor: style.getPropertyValue('--bg-tertiary').trim(),
      font_color: style.getPropertyValue('--text-primary').trim(),
      axis_color: style.getPropertyValue('--text-secondary').trim(),
      grid_color: style.getPropertyValue('--border-color').trim()
    };
  }

  private updateTheme(): void {
    const colors = this.getThemeColors();

    const layoutUpdate: Partial<Plotly.Layout> = {
      paper_bgcolor: colors.paper_bgcolor,
      plot_bgcolor: colors.plot_bgcolor,
      font: { color: colors.font_color },
      'title.font.color': colors.font_color,
      'xaxis.color': colors.axis_color,
      'yaxis.color': colors.axis_color,
      'xaxis.gridcolor': colors.grid_color,
      'yaxis.gridcolor': colors.grid_color
    };

    Plotly.relayout(this.omegaDiv, layoutUpdate);
    Plotly.relayout(this.energyDiv, layoutUpdate);
  }

  private initializePlots(): void {
    const colors = this.getThemeColors();

    // Angular velocity plot
    const omegaTraces: Partial<Plotly.PlotData>[] = [
      {
        x: [],
        y: [],
        name: 'ωₓ',
        type: 'scatter',
        mode: 'lines',
        line: { color: '#ff4444', width: 2 }
      },
      {
        x: [],
        y: [],
        name: 'ωᵧ',
        type: 'scatter',
        mode: 'lines',
        line: { color: '#44ff44', width: 2 }
      },
      {
        x: [],
        y: [],
        name: 'ω_z',
        type: 'scatter',
        mode: 'lines',
        line: { color: '#4444ff', width: 2 }
      }
    ];

    const omegaLayout: Partial<Plotly.Layout> = {
      title: {
        text: 'Angular Velocity (Body Frame)',
        font: { color: colors.font_color, size: 14 }
      },
      xaxis: {
        title: 'Time (s)',
        color: colors.axis_color,
        gridcolor: colors.grid_color
      },
      yaxis: {
        title: 'ω (rad/s)',
        color: colors.axis_color,
        gridcolor: colors.grid_color
      },
      paper_bgcolor: colors.paper_bgcolor,
      plot_bgcolor: colors.plot_bgcolor,
      font: { color: colors.font_color },
      margin: { l: 50, r: 20, t: 40, b: 40 },
      showlegend: true,
      legend: {
        x: 1,
        xanchor: 'right',
        y: 1,
        bgcolor: 'rgba(0,0,0,0.5)'
      }
    };

    Plotly.newPlot(this.omegaDiv, omegaTraces, omegaLayout, {
      responsive: true,
      displayModeBar: false
    });

    // Energy plot
    const energyTraces: Partial<Plotly.PlotData>[] = [
      {
        x: [],
        y: [],
        name: 'Total',
        type: 'scatter',
        mode: 'lines',
        line: { color: '#ffaa00', width: 2 }
      },
      {
        x: [],
        y: [],
        name: 'Kinetic',
        type: 'scatter',
        mode: 'lines',
        line: { color: '#00ffaa', width: 2, dash: 'dot' }
      },
      {
        x: [],
        y: [],
        name: 'Potential',
        type: 'scatter',
        mode: 'lines',
        line: { color: '#aa00ff', width: 2, dash: 'dot' }
      }
    ];

    const energyLayout: Partial<Plotly.Layout> = {
      title: {
        text: 'Energy',
        font: { color: colors.font_color, size: 14 }
      },
      xaxis: {
        title: 'Time (s)',
        color: colors.axis_color,
        gridcolor: colors.grid_color
      },
      yaxis: {
        title: 'Energy (J)',
        color: colors.axis_color,
        gridcolor: colors.grid_color
      },
      paper_bgcolor: colors.paper_bgcolor,
      plot_bgcolor: colors.plot_bgcolor,
      font: { color: colors.font_color },
      margin: { l: 50, r: 20, t: 40, b: 40 },
      showlegend: true,
      legend: {
        x: 1,
        xanchor: 'right',
        y: 1,
        bgcolor: 'rgba(0,0,0,0.5)'
      }
    };

    Plotly.newPlot(this.energyDiv, energyTraces, energyLayout, {
      responsive: true,
      displayModeBar: false
    });
  }

  /**
   * Add new sample to plots
   */
  addSample(sample: Sample): void {
    // Add to data buffers
    this.data.time.push(sample.t);
    this.data.omega_x.push(sample.omega[0]);
    this.data.omega_y.push(sample.omega[1]);
    this.data.omega_z.push(sample.omega[2]);
    this.data.energy.push(sample.energy);
    this.data.kinetic.push(sample.kinetic ?? 0);
    this.data.potential.push(sample.potential ?? 0);

    // Maintain window size
    if (this.data.time.length > this.windowSize) {
      this.data.time.shift();
      this.data.omega_x.shift();
      this.data.omega_y.shift();
      this.data.omega_z.shift();
      this.data.energy.shift();
      this.data.kinetic.shift();
      this.data.potential.shift();
    }

    // Update plots periodically (not every frame for performance)
    this.updateCounter++;
    if (this.updateCounter >= this.updateInterval) {
      this.updateCounter = 0;
      this.updatePlots();
    }
  }

  /**
   * Update Plotly charts with current data
   */
  private updatePlots(): void {
    // Update angular velocity plot
    Plotly.update(
      this.omegaDiv,
      {
        x: [this.data.time, this.data.time, this.data.time],
        y: [this.data.omega_x, this.data.omega_y, this.data.omega_z]
      },
      {}
    );

    // Update energy plot
    Plotly.update(
      this.energyDiv,
      {
        x: [this.data.time, this.data.time, this.data.time],
        y: [this.data.energy, this.data.kinetic, this.data.potential]
      },
      {}
    );
  }

  /**
   * Clear all plot data
   */
  clear(): void {
    this.data = {
      time: [],
      omega_x: [],
      omega_y: [],
      omega_z: [],
      energy: [],
      kinetic: [],
      potential: []
    };

    this.updatePlots();
  }

  /**
   * Set window size (number of samples to display)
   */
  setWindowSize(size: number): void {
    this.windowSize = size;
  }

  /**
   * Set update interval (update every N samples)
   */
  setUpdateInterval(interval: number): void {
    this.updateInterval = interval;
  }

  /**
   * Resize plots (call on window resize)
   */
  resize(): void {
    Plotly.Plots.resize(this.omegaDiv);
    Plotly.Plots.resize(this.energyDiv);
  }
}
